export type CookieConsentStatus = 'accepted' | 'declined' | null

export interface CookieConsentRecord {
  status: 'accepted' | 'declined'
  timestamp: number
  version: number
}

export const CONSENT_STORAGE_KEY = 'c4_cookie_consent'
export const CONSENT_EVENT_NAME = 'c4-cookie-consent'
export const CURRENT_CONSENT_VERSION = 1

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

/**
 * Получить текущий статус согласия на использование cookie
 */
export function getCookieConsent(): CookieConsentStatus {
  if (!isBrowser()) return null

  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!raw) return null

    // Поддержка как JSON-структуры, так и простого строкового значения
    if (raw === 'accepted' || raw === 'declined') {
      return raw
    }

    const parsed = JSON.parse(raw) as Partial<CookieConsentRecord>
    if (parsed && (parsed.status === 'accepted' || parsed.status === 'declined')) {
      return parsed.status
    }
    return null
  } catch {
    return null
  }
}

/**
 * Проверить, принял ли пользователь решение по cookie
 */
export function hasConsentDecision(): boolean {
  return getCookieConsent() !== null
}

/**
 * Сохранить выбор пользователя (принято или отклонено)
 */
export function setCookieConsent(status: 'accepted' | 'declined'): void {
  if (!isBrowser()) return

  const record: CookieConsentRecord = {
    status,
    timestamp: Date.now(),
    version: CURRENT_CONSENT_VERSION,
  }

  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record))
  } catch (err) {
    console.warn('Не удалось сохранить статус cookie-согласия в localStorage:', err)
  }

  window.dispatchEvent(
    new CustomEvent(CONSENT_EVENT_NAME, {
      detail: { status },
    }),
  )
}

/**
 * Сбросить согласие (например, при повторном вызове настроек или в тестах)
 */
export function resetCookieConsent(): void {
  if (!isBrowser()) return

  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY)
  } catch (err) {
    console.warn('Не удалось удалить статус cookie-согласия:', err)
  }

  window.dispatchEvent(
    new CustomEvent(CONSENT_EVENT_NAME, {
      detail: { status: null },
    }),
  )
}

/**
 * Подписаться на изменение согласия
 */
export function onCookieConsentChange(callback: (status: CookieConsentStatus) => void): () => void {
  if (!isBrowser()) {
    return () => {}
  }

  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<{ status: CookieConsentStatus }>
    callback(customEvent.detail?.status ?? null)
  }

  window.addEventListener(CONSENT_EVENT_NAME, handler)
  return () => {
    window.removeEventListener(CONSENT_EVENT_NAME, handler)
  }
}
